import React, { useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";

const CATEGORIES = [
  { id: "damage", label: "Damage" },
  { id: "payment_refund", label: "Payment / refund" },
  { id: "no_show", label: "No-show" },
  { id: "fraud", label: "Fraud" },
  { id: "other", label: "Other" },
];

export default function ReportIssueModal({ booking, open, onClose, onSubmitted }) {
  const [category, setCategory] = useState("other");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory("other");
    setSubject("");
    setBody("");
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const submit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Please add a subject and description");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/support/cases`,
        {
          booking_id: booking?.booking_id || undefined,
          category,
          subject: subject.trim(),
          body: body.trim(),
        },
        { withCredentials: true },
      );
      toast.success("Issue reported — our team will follow up");
      reset();
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit report");
    } finally {
      setSubmitting(false);
    }
  };

  const vehicleLabel = booking?.vehicle_snapshot
    ? `${booking.vehicle_snapshot.year || ""} ${booking.vehicle_snapshot.make || ""} ${booking.vehicle_snapshot.model || ""}`.trim()
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-white border border-[var(--zc-border)] max-w-lg" data-testid="report-issue-modal">
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl flex items-center gap-2">
            <AlertCircle size={20} className="text-[var(--zc-accent)]" />
            Report an issue
          </DialogTitle>
        </DialogHeader>
        {booking?.booking_id && (
          <div className="text-sm text-[var(--zc-text-2)]">
            Booking <span className="font-mono text-xs">{booking.booking_id}</span>
            {vehicleLabel && <> · {vehicleLabel}</>}
          </div>
        )}
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="zc-input w-full h-10 px-3 text-sm mt-1"
              data-testid="report-issue-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="zc-input w-full h-10 px-3 text-sm mt-1"
              placeholder="Brief summary"
              maxLength={200}
              data-testid="report-issue-subject"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Description</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="zc-input w-full p-3 text-sm mt-1 h-28"
              placeholder="What happened? Include dates, amounts, or photos you uploaded elsewhere."
              data-testid="report-issue-body"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={handleClose} className="px-4 h-10 zc-btn-ghost text-sm" disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-4 h-10 zc-btn-primary text-sm disabled:opacity-50"
            data-testid="report-issue-submit"
          >
            {submitting ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
